import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { ApplicationinsightsAngularpluginErrorService } from '@microsoft/applicationinsights-angularplugin-js';
import { ACCOUNTLINK_API_BASE_URL } from '@r1-development/accountlink-angular-client';
import { FILE_SHED_API_BASE_URL } from '@r1-development/fileshed-angular-client';
import { RESOLUTION_SERVICES_API_BASE_URL } from '@r1-development/resolution-mechanicalturk-api-client';
import {
  SHELL_HOME_CONFIG_RI,
  ShellHomeConfigRi,
} from '@r1-development/rps-shared-ui';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { APOLLO_NAMED_OPTIONS, Apollo, NamedOptions } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { appRoutes } from './app.routes';
import { initializeAppConfig } from './shared/app-config/app-config.factory';
import { AppConfigService } from './shared/app-config/app-config.service';
import { ApiParametersToken } from './shared/features/header/services/menu-items-api.service';
import { forbiddenInterceptorFn } from './shared/interceptors/interceptors';
import { refreshTokenInterceptorFn } from './shared/interceptors/refresh-token-interceptors';
import { IndexedDBStorage } from './shared/services/storage/indexed-db-storage';
import { UI_STORAGE } from './shared/services/storage/storage.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from '@angular/cdk/overlay';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        subscriptSizing: 'dynamic', // Can cause layout shifting if errors show up
      },
    },
    {
      provide: MAT_CARD_CONFIG,
      useValue: { appearance: 'outlined' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2500,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      },
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        panelWidth: 'auto',
        hideSingleSelectionIndicator: true,
      },
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    provideRouter(appRoutes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptors([
        refreshTokenInterceptorFn,
        authHttpInterceptorFn,
        forbiddenInterceptorFn,
      ])
    ),
    provideAuth0(),
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    {
      provide: ApiParametersToken,
      useFactory: (appConfigService: AppConfigService) => {
        return {
          apiUrl: appConfigService.config.riUserManagementUrl + '/api/menu',
          params: {
            localMenu: appConfigService.config.environment === 'local',
          },
        };
      },
      deps: [AppConfigService],
    },
    provideAppInitializer(() => {
      const initializerFn = initializeAppConfig(inject(AppConfigService));
      return initializerFn();
    }),
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    {
      // For saving user's progress there is a storage
      // provided here. If there is a need to change
      // type of storage from localStorage to something else
      // (like sessionStorage, indexedDb) then create a class
      // and implement "Storage" interface. In the end
      // replace LocalStorage to just newly created class
      provide: UI_STORAGE,
      useClass: IndexedDBStorage,
    },
    {
      provide: RESOLUTION_SERVICES_API_BASE_URL,
      useFactory: (appConfigService: AppConfigService) => {
        return appConfigService.config.resolutionServiceBaseUrl;
      },
      deps: [AppConfigService],
    },
    {
      provide: FILE_SHED_API_BASE_URL,
      useFactory: (appConfigService: AppConfigService) => {
        return appConfigService.config.fileshedServiceBaseUrl;
      },
      deps: [AppConfigService],
    },
    {
      provide: ACCOUNTLINK_API_BASE_URL,
      useFactory: (appConfigService: AppConfigService) => {
        return appConfigService.config.accountLinkClientServiceBaseUrl;
      },
      deps: [AppConfigService],
    },
    {
      provide: SHELL_HOME_CONFIG_RI,
      useFactory: (appConfigService: AppConfigService) => {
        return {
          isLocal: appConfigService.config.environment === 'local',
          riUserManagementApiUrl: appConfigService.config.riUserManagementUrl,
          adminUrl: '',
        } as ShellHomeConfigRi;
      },
      deps: [AppConfigService],
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory(
        httpLink: HttpLink,
        appConfigService: AppConfigService
      ): NamedOptions {
        return {
          // Account Link Client
          accountLinkClient: {
            link: ApolloLink.from([
              httpLink.create({
                uri: appConfigService.config.accountLinkGraphQlUrl,
              }),
            ]),
            cache: new InMemoryCache(),
            defaultOptions: {
              watchQuery: {
                fetchPolicy: 'no-cache',
              },
              query: {
                fetchPolicy: 'no-cache',
              },
            },
          },
        };
      },
      deps: [HttpLink, AppConfigService],
    },
    Apollo,
  ],
};
